import {Breadcrumbs} from '@primer/react'
import {clsx} from 'clsx'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import {getOcticon} from '../../utils/octicons'
import type {ContextCrumb, LocalNavigationTab} from '../../types'
import styles from './styles.module.css'
import {useTopNavContext} from '../../primitives/TopNav'
import {FeatureFlags} from '@primer/react/experimental'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RepoPicker} from './RepoPicker'

export interface ContextRegionPayload {
  /** Array of breadcrumb items for the context region */
  crumbs: ContextCrumb[]
  /** Array of local navigation tabs (e.g., for repository navigation) */
  localNavigation?: LocalNavigationTab[]
  /** A string identifier that indicates what links should be active according to the route's controller */
  selectedLink?: string
}

export interface ContextRegionProps {
  crumbs?: ContextCrumb[]
}

export function ContextRegion({crumbs}: ContextRegionProps) {
  const {sendItemClick} = useGlobalNavAnalytics('CONTEXT_REGION')
  const {searchOpen, isSearchPage} = useTopNavContext()

  if (!crumbs || crumbs.length === 0 || isSearchPage || searchOpen) {
    return null
  }

  const lastCrumbIsRepo = crumbs[crumbs.length - 1]?.crumb_type === 'repository'
  const showRepoPicker = lastCrumbIsRepo && isFeatureEnabled('global_nav_repo_picker_experiment')

  const repositoryPickerRepoCrumb = showRepoPicker ? crumbs.find(crumb => crumb.crumb_type === 'repository') : undefined

  // find this repo's owner crumb so we can find other repos under the same owner
  const ownerCrumb =
    showRepoPicker && repositoryPickerRepoCrumb ? crumbs[crumbs.indexOf(repositoryPickerRepoCrumb) - 1] : undefined

  return (
    <FeatureFlags flags={{primer_react_breadcrumbs_overflow_menu: true}}>
      <Breadcrumbs overflow="menu">
        {crumbs.map((crumb, index) => {
          if (ownerCrumb && repositoryPickerRepoCrumb && crumb.crumb_type === 'repository') {
            // when the repo picker experiment is enabled, we don't show the repository crumb in the context region
            return (
              <RepoPicker
                key={repositoryPickerRepoCrumb.label}
                repositoryCrumb={repositoryPickerRepoCrumb}
                ownerCrumb={ownerCrumb}
              />
            )
          }

          const href = crumb.href || crumb.path
          const tagType = href ? 'a' : 'span'
          const isLast = index === crumbs.length - 1
          const Octicon = getOcticon(crumb.octicon)

          return (
            <Breadcrumbs.Item
              key={href || crumb.label}
              href={href}
              as={tagType}
              className={clsx({
                [styles.contextCrumb]: true,
                [styles.contextCrumbStatic]: tagType === 'span',
              })}
              onClick={() => {
                sendItemClick('CONTEXT_REGION_CRUMB', `position:${index + 1}`)
              }}
            >
              <span className={isLast ? styles.contextCrumbLast : ''}>{crumb.label}</span>
              {Octicon && <Octicon size={12} className={styles.trailingIcon} />}
            </Breadcrumbs.Item>
          )
        })}
      </Breadcrumbs>
    </FeatureFlags>
  )
}

try{ ContextRegion.displayName ||= 'ContextRegion' } catch {}
try{ Octicon.displayName ||= 'Octicon' } catch {}